/* ----------------------------------------------------------------------------
 * wakeChime — the hands-free wake CUE (owner decision 10-10-2026, recorded
 * docs/brain/governance/owner-decision-aura-publish-choice-2026-10-10.md, last
 * section; verbatim label: "A: Chime, not speech (Recommended)").
 *
 * On a hands-free wake Aura plays this short chime (and shows the existing
 * listening cue) INSTEAD of speaking "How can I help?". Evidence for the
 * decision: false wakes score as high as real ones (0.82-0.99 vs 0.63-0.9994,
 * reports-findings/aura-voice-regression-2026-10-10/aura-false-wake-diagnosis-
 * and-fix-2026-10-10_1518.md §3/§4), so the only lever left is what a false
 * wake SOUNDS like.
 *
 * The chime is GENERATED in memory (a ~170 ms two-note sine WAV, 16-bit mono
 * PCM): no asset fetch, no provider call, no network at all. It is played
 * through the SAME AuraAudioPlayer as every other Aura sound, so it gets the
 * same output volume (TTS_PLAYBACK_VOLUME) and the controller skips it when
 * Aura's speaker is muted. Pure + deterministic (byte-identical every call).
 * -------------------------------------------------------------------------- */

export const WAKE_CHIME_MIME = 'audio/wav';
/** Upper bound (ms) the wake turn waits for the chime's playback-end event
 *  before opening the capture anyway (chime itself is ~170 ms). */
export const WAKE_CHIME_MAX_WAIT_MS = 600;
const SAMPLE_RATE = 22050;
/** Two short notes (Hz, ms): a soft rising "ding-dong". */
const NOTES: readonly [number, number][] = [[880, 80], [1320, 90]];
/** Peak amplitude (0..1) before the player's own volume stage. Kept low: a cue, not an alert. */
const PEAK = 0.22;

let cached: Uint8Array | null = null;

/** The chime as WAV bytes (cached; a fresh copy is never needed — the player
 *  does not mutate the buffer). */
export function wakeChimeBytes(): Uint8Array {
  if (cached) return cached;
  const samples: number[] = [];
  for (const [freq, ms] of NOTES) {
    const n = Math.round((SAMPLE_RATE * ms) / 1000);
    const fade = Math.round(SAMPLE_RATE * 0.008); // 8 ms attack/release, no clicks
    for (let i = 0; i < n; i++) {
      const env = Math.min(1, i / fade, (n - 1 - i) / fade);
      samples.push(Math.sin((2 * Math.PI * freq * i) / SAMPLE_RATE) * PEAK * env);
    }
  }
  const dataLen = samples.length * 2;
  const buf = new ArrayBuffer(44 + dataLen);
  const v = new DataView(buf);
  const str = (o: number, s: string) => { for (let i = 0; i < s.length; i++) v.setUint8(o + i, s.charCodeAt(i)); };
  str(0, 'RIFF'); v.setUint32(4, 36 + dataLen, true); str(8, 'WAVE');
  str(12, 'fmt '); v.setUint32(16, 16, true); v.setUint16(20, 1, true); v.setUint16(22, 1, true);
  v.setUint32(24, SAMPLE_RATE, true); v.setUint32(28, SAMPLE_RATE * 2, true); v.setUint16(32, 2, true); v.setUint16(34, 16, true);
  str(36, 'data'); v.setUint32(40, dataLen, true);
  samples.forEach((s, i) => v.setInt16(44 + i * 2, Math.max(-1, Math.min(1, s)) * 0x7fff, true));
  cached = new Uint8Array(buf);
  return cached;
}
